import BrandSlider from "@/components/Ui/Pages/BrandsSlider/BrandsSlider";
import CategoryCard from "@/components/Ui/Pages/CategoryCard/CategoryCard";
import { removeTags, t, localePrefix } from "@/helper/helper";
import { baseURL } from "@/services/api";
import { ICategoryGeneric } from "global-types/global.types";
import { GetStaticPaths, GetStaticProps } from "next";
import SpecialProduct from "@/components/Ui/Pages/SpecialProducts/SpecialProducts";
import SideBannerCarousel from "@/components/Ui/Pages/SideBannerCarousel/SideBannerCarousel";
import { NextSeo } from "next-seo";
import { SITE_NAME, SITE_URL } from "@/helper/constant";
import { useRouter } from "next/router";
import axios from "axios";
import useTopDiscount from "@/hooks/common/useTopDiscount";
import useNewProducts from "@/hooks/common/useNewProducts";
import useBestSeller from "@/hooks/common/useBestSeller";
import Spinner from "@/components/Ui/Tools/Spinner/Spinner";
import styled from "styled-components";
import useBrands from "@/hooks/common/useBrands";
import useBreadCrumbs from "@/hooks/tools/useBreadCrumb";
import Link from "next/link";
import Head from "next/head";

interface Props {
  locale: string;
  categories: {
    data: ICategoryGeneric;
  };
  slug: string[];
}

export default function Categories({ locale, categories, slug }: Props) {
  // router
  const { asPath } = useRouter();

  // top Discount
  const { topDiscount, isLoadingTopDiscount } = useTopDiscount(slug);
  const { newProducts, isLoadingNewProducts } = useNewProducts(slug);
  const { bestSeller, isLoadingBestSeller } = useBestSeller(slug);
  const { brands, isLoadingBrands } = useBrands(slug);
  const { breadCrumbs } = useBreadCrumbs(categories?.data.id);

  // Create breadcrumb schema
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      {
        "@type": "ListItem",
        position: 1,
        name: t("homePage", locale),
        item: `${SITE_URL}${localePrefix(locale)}`,
      },
      ...(breadCrumbs?.data.parent
        ? breadCrumbs.data.parent
            .slice()
            .reverse()
            .map((item, index) => ({
              "@type": "ListItem",
              position: index + 2,
              name: item.name,
              item: `${SITE_URL}${localePrefix(locale)}/categories/${item.slug}`,
            }))
        : []),
      {
        "@type": "ListItem",
        position: (breadCrumbs?.data.parent?.length || 0) + 2,
        name: categories?.data?.name,
        item: `${SITE_URL}${localePrefix(locale)}/categories/${categories?.data?.slug}`,
      },
    ],
  };

  // Create ItemList schema for subcategories
  const itemListSchema = categories?.data?.children?.length
    ? {
        "@context": "https://schema.org",
        "@type": "ItemList",
        name: categories?.data?.name,
        description:
          categories?.data?.meta_desc ||
          removeTags(categories?.data?.desc).substring(0, 300),
        url: `${SITE_URL}${localePrefix(locale)}/categories/${categories?.data?.slug}`,
        numberOfItems: categories?.data?.children?.length,
        itemListElement: categories?.data?.children?.map((child, index) => ({
          "@type": "ListItem",
          position: index + 1,
          name: child.name,
          url: child.children?.length
            ? `${SITE_URL}${localePrefix(locale)}/categories/${child.slug}`
            : `${SITE_URL}${localePrefix(locale)}/product-list/${child.slug}`,
        })),
      }
    : null;

  return (
    <>
      <Head>
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumbSchema) }}
        />
        {itemListSchema && (
          <script
            type="application/ld+json"
            dangerouslySetInnerHTML={{
              __html: JSON.stringify(itemListSchema),
            }}
          />
        )}
      </Head>
      <NextSeo
        title={`${categories?.data?.meta_title || categories?.data?.name}`}
        // @ts-ignore
        description={
          categories?.data?.meta_desc?.substring(0, 160) ||
          removeTags(categories?.data?.desc).substring(0, 160)
        }
        additionalMetaTags={[
          {
            name: "title",
            content: `${
              categories?.data?.meta_title || categories?.data?.name
            } | ${SITE_NAME}`,
          },
        ]}
        canonical={SITE_URL + localePrefix(locale) + asPath}
        openGraph={{
          url: SITE_URL + localePrefix(locale) + asPath,
          title: `${
            categories?.data?.meta_title || categories?.data?.name
          } | ${SITE_NAME}`,
          // @ts-ignore
          description:
            categories?.data?.meta_desc?.substring(0, 300) ||
            removeTags(categories?.data?.desc).substring(0, 300),
          images: [
            {
              url: categories?.data?.og_image || "",
            },
          ],
          siteName: SITE_URL,
        }}
      />
      <Wrapper>
        {!slug ? (
          <SpinnerWrapper>
            <Spinner />
          </SpinnerWrapper>
        ) : (
          <div className="category-page">
            <div className="breadcrumb">
              <Link href={`${localePrefix(locale) || "/"}`}>{t("homePage", locale)}</Link>
              <span className="breadcrumb-seperator" />
              {breadCrumbs?.data.parent ? (
                breadCrumbs.data.parent.reverse().map((item, index) => (
                  <Link
                    href={`${localePrefix(locale)}/categories/${item.slug}`}
                    key={index.toString() + item.name}
                  >
                    {item.name}
                  </Link>
                ))
              ) : (
                <></>
              )}
              <span className="breadcrumb-seperator" />
              <Link href={`${localePrefix(locale)}/categories/${categories.data.slug}`}>
                {categories.data.name}
              </Link>
            </div>
            <div className="category-wrapper">
              <div className="category-wrapper-child">
                <div>
                  <div className="category-class-name">
                    <h1 className="category-page-title">
                      {categories?.data?.name}
                    </h1>
                  </div>
                  <div className="main-section">
                    <CategoryCard
                      data={categories}
                      locale={locale}
                      slug={`${slug}`}
                    />
                  </div>
                </div>
              </div>
              {/* <div className="side-section" style={{ marginBottom: "24px" }}>
                {!isLoadingTopDiscount ? (
                  topDiscount?.data?.length !== 0 ? (
                    <SideBannerCarousel data={topDiscount!} locale={locale} />
                  ) : (
                    <></>
                  )
                ) : (
                  <SpinnerWrap className="first">
                    <Spinner />
                  </SpinnerWrap>
                )}
                {!isLoadingNewProducts ? (
                  newProducts?.data?.length !== 0 ? (
                    <div className="category-new-products">
                      <div className="header">{t("newProducts", locale)}</div>
                      <ul className="cat-product-list">
                        {newProducts?.data?.map((item, index) => (
                          <SpecialProduct
                            key={index.toString() + item.title}
                            data={item}
                            locale={locale}
                          />
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <></>
                  )
                ) : (
                  <SpinnerWrap>
                    <Spinner />
                  </SpinnerWrap>
                )}
                {!isLoadingBestSeller ? (
                  bestSeller?.data.length !== 0 ? (
                    <div className="category-most-sale-products">
                      <div className="header">{t("bestSeller", locale)}</div>
                      <ul className="cat-product-list">
                        {bestSeller?.data?.map((item, index) => (
                          <SpecialProduct
                            key={index.toString() + item.title}
                            data={item}
                            locale={locale}
                          />
                        ))}
                      </ul>
                    </div>
                  ) : (
                    <></>
                  )
                ) : (
                  <SpinnerWrap>
                    <Spinner />
                  </SpinnerWrap>
                )}
              </div> */}
            </div>
            {!isLoadingBrands! && brands?.data ? (
              brands?.data?.length !== 0 ? (
                <BrandSlider data={brands!} locale={locale} mt={24} mb={25} />
              ) : (
                <></>
              )
            ) : (
              <></>
            )}
            <div
              className="category-class-name"
              style={{
                marginTop: 25,
              }}
            >
              <div
                className="category-page-desc"
                dangerouslySetInnerHTML={{
                  __html: categories?.data?.desc,
                }}
              />
            </div>
          </div>
        )}
      </Wrapper>
    </>
  );
}

const SpinnerWrapper = styled.div`
  height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const Wrapper = styled.div`
  min-height: 80vh;

  .category-wrapper {
    display: block !important;
  }

  .main-section {
    grid-column: 1 / -1 !important;
    grid-template-columns: repeat(4, 1fr) !important;
    max-width: 100%;

    .category-card {
      max-width: 100%;
    }

    @media (max-width: 1024px) {
      grid-template-columns: repeat(3, 1fr) !important;
    }

    @media (max-width: 768px) {
      grid-template-columns: repeat(2, 1fr) !important;
    }

    @media (max-width: 480px) {
      grid-template-columns: repeat(1, 1fr) !important;
    }
  }
`;

const SpinnerWrap = styled.div`
  width: 310px;
  display: flex;
  justify-content: center;
  margin-bottom: 100px;

  &.first {
    margin-top: 40px;
  }

  img {
    width: 50px;
  }
`;

export const getStaticPaths: GetStaticPaths = async (ctx) => {
  return {
    paths: [],
    fallback: "blocking",
  };
};

export const getStaticProps: GetStaticProps = async (ctx) => {
  const { locale } = ctx;
  const { slug } = ctx.params!;

  // categories
  const categories = await axios.get(baseURL + `/category/detail/${slug}`, {
    headers: {
      "x-localization": locale ? locale : "az",
    },
  });

  return {
    props: {
      categories: categories.data,
      locale,
      slug,
    },
    revalidate: 60,
  };
};
